import React, { useState, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FormContext } from 'react-hook-form';
import { Link, Redirect, useLocation } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import MuiLink from '@material-ui/core/Link';
import { Auth } from 'aws-amplify';
import { validatePassword } from '../../../utils/cognitoValidation';
import UnathorizedLayout from '../../shared/layout/UnauthorizedLayout';
import FormPasswordInput from '../../shared/form/FormPasswordInput';
import { AlertContext } from '../../shared/app/AlertProvider';
import { IStatus, IError, cognitoErrorType } from '../../../types';
import { FormBox } from '../../shared/form-box';
import { useStyles } from '../login/styles';

const ResetPassword = () => {
  const [submitStatus, setSubmitStatus] = useState<IStatus>({
    value: 'idle',
    code: null,
  });
  const [redirect, setRedirect] = useState(false);
  const classes = useStyles();

  const { t } = useTranslation();

  const { showAlert } = useContext(AlertContext);

  const methods = useForm({
    submitFocusError: false,
  });

  const { search } = useLocation();

  const { watch, handleSubmit } = methods;

  const onSubmit = useCallback(
    (data) => {
      const username = new URLSearchParams(search).get('user_name') || '';
      const confirmationCode =
        new URLSearchParams(search).get('confirmation_code') || '';
      const { password, repeatPassword } = data;
      if (password !== repeatPassword) {
        throw new Error('passwords must match');
      }
      setSubmitStatus({ value: 'pending', code: null });
      Auth.forgotPasswordSubmit(username, confirmationCode, password)
        .then(() => {
          showAlert({
            severity: 'success',
            message: t('ResetPassword.successMsg'),
          });
          setRedirect(true);
        })
        .catch((err: IError) => {
          if (
            err.__type === cognitoErrorType.EXPIRED_CODE_EXCEPTION ||
            err.__type === cognitoErrorType.LIMIT_EXCEED_EXCEPTION
          ) {
            showAlert({
              severity: 'error',
              message: t(
                'v2.resetPassword.snackbar_label_error_exceptions_limitTimeAndExpiredLink',
              ),
            });
          } else {
            showAlert({
              severity: 'error',
              message: t('v2.global.snackbar_label_error_somethingWentWrong'),
            });
          }
          setSubmitStatus({ value: 'rejected', code: err.status });
        });
    },
    [search, showAlert, t],
  );

  if (redirect) {
    return <Redirect to="/login" />;
  }

  return (
    <UnathorizedLayout
      title={t('ResetPassword.title')}
      subtitle={t('ResetPassword.subtitle')}
    >
      <FormContext {...methods}>
        <FormBox
          component="form"
          p={3}
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {/* @ts-ignore next */}
              <FormPasswordInput
                id="reset-password-password"
                name="password"
                label={t('ResetPassword.passwordLabel')}
                placeholder={t('ResetPassword.passwordPlaceholder')}
                validate={(value: string) => {
                  if (
                    validatePassword(value) &&
                    validatePassword(value).includes('errorHandling')
                  ) {
                    return t(validatePassword(value));
                  }
                }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              {/* @ts-ignore next */}
              <FormPasswordInput
                id="reset-password-repeat"
                name="repeatPassword"
                label={t('ResetPassword.passwordRepeatLabel')}
                placeholder={t('ResetPassword.passwordRepeatPlaceholder')}
                required
                validate={(value: string) => {
                  if (value !== watch('password')) {
                    return 'v2.global.textField_errorHandling_helperText_passwordsDontMatch';
                  }
                  if (
                    validatePassword(value) &&
                    validatePassword(value).includes('errorHandling')
                  ) {
                    return t(validatePassword(value));
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box pt={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitStatus.value === 'pending'}
                >
                  {t('ResetPassword.submit')}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              {t('v2.global.label_userHasAnAccount')}
              <MuiLink
                component={Link}
                to="/registration"
                variant="body1"
                className={classes.links}
              >
                {t('v2.logIn.button_label_logIn')}
              </MuiLink>
            </Grid>
            <Grid item xs={12}>
              {t('v2.global.label_needHelp')}
              <MuiLink
                component="a"
                variant="body1"
                className={classes.links}
                href={`mailto:${process.env.REACT_APP_CONTACT_EMAIL}`}
              >
                {t('v2.global.button_needHelp')}
              </MuiLink>
            </Grid>
          </Grid>
        </FormBox>
      </FormContext>
    </UnathorizedLayout>
  );
};

export default ResetPassword;
